import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Shield, Brain, ExternalLink, AlertCircle } from 'lucide-react';

/**
 * AI Data Consent Dialog
 * Required for Apple App Store compliance - must be shown before any AI features are used
 * Explains what data is sent, who it's sent to, and obtains explicit user permission
 */
const AIConsentDialog = ({ isOpen, onAccept, onDecline, feature = 'AI features' }) => {
  const [hasReadPolicy, setHasReadPolicy] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">
              AI Data Sharing Consent
            </h2>
            <p className="text-sm text-[#57534E] mt-2">
              Please review how your data is used with {feature}
            </p>
          </div>

          {/* Data Disclosure Section */}
          <div className="space-y-4">
            {/* What data is sent */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 text-sm">What Data Is Shared</h3>
                  <p className="text-blue-800 text-sm mt-1">
                    When you use AI features, the following data may be sent:
                  </p>
                  <ul className="list-disc list-inside text-blue-800 text-sm mt-2 space-y-1">
                    <li>Your questions and messages to the AI</li>
                    <li>Relevant health context you've entered (symptoms, medications)</li>
                    <li>No personally identifiable information (name, email) is sent</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Who receives the data */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-900 text-sm">Who Receives This Data</h3>
                  <p className="text-amber-800 text-sm mt-1">
                    Your data is sent to <strong>OpenAI</strong> to process your requests and generate responses.
                  </p>
                  <ul className="list-disc list-inside text-amber-800 text-sm mt-2 space-y-1">
                    <li>OpenAI does not use your data to train their AI models</li>
                    <li>Data is encrypted in transit and at rest</li>
                    <li>OpenAI is contractually bound to protect your data</li>
                  </ul>
                  <a 
                    href="https://openai.com/policies/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-amber-700 underline text-sm mt-2 inline-block"
                  >
                    View OpenAI's Privacy Policy
                  </a>
                </div>
              </div>
            </div>

            {/* Data protection */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900 text-sm">Your Rights & Control</h3>
                  <ul className="list-disc list-inside text-green-800 text-sm mt-1 space-y-1">
                    <li>You can withdraw consent at any time in Settings</li>
                    <li>AI features will be disabled if you decline</li>
                    <li>Your existing data remains protected regardless of choice</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
            <Checkbox 
              id="ai-consent-checkbox"
              checked={hasReadPolicy}
              onCheckedChange={setHasReadPolicy}
              className="mt-0.5"
            />
            <label htmlFor="ai-consent-checkbox" className="text-sm text-[#57534E] cursor-pointer">
              I understand that my data will be shared with OpenAI to provide AI-powered features, 
              and I consent to this data sharing.
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onDecline}
              variant="outline"
              className="flex-1"
              data-testid="ai-consent-decline"
            >
              Decline
            </Button>
            <Button
              onClick={onAccept}
              disabled={!hasReadPolicy}
              className="flex-1 bg-primary hover:bg-primary/90"
              data-testid="ai-consent-accept"
            >
              Accept & Continue
            </Button>
          </div>

          <p className="text-xs text-center text-[#A8A29E]">
            You can change this preference anytime in Settings → Privacy
          </p>
        </div>
      </Card>
    </div>
  );
};

/**
 * Hook to manage AI consent state
 */
export const useAIConsent = () => {
  const [hasConsent, setHasConsent] = useState(null);
  const [showConsentDialog, setShowConsentDialog] = useState(false);

  useEffect(() => {
    // Check if user has already given/denied consent
    const consent = localStorage.getItem('aiDataConsent');
    if (consent === 'accepted') {
      setHasConsent(true);
    } else if (consent === 'declined') {
      setHasConsent(false);
    } else {
      setHasConsent(null); // Not yet asked
    }
  }, []);

  const requestConsent = () => {
    if (hasConsent === null) {
      setShowConsentDialog(true);
      return false;
    }
    return hasConsent;
  };

  const acceptConsent = () => {
    localStorage.setItem('aiDataConsent', 'accepted');
    localStorage.setItem('aiDataConsentDate', new Date().toISOString());
    setHasConsent(true);
    setShowConsentDialog(false);
  };

  const declineConsent = () => {
    localStorage.setItem('aiDataConsent', 'declined');
    localStorage.setItem('aiDataConsentDate', new Date().toISOString());
    setHasConsent(false);
    setShowConsentDialog(false);
  };

  const resetConsent = () => {
    localStorage.removeItem('aiDataConsent');
    localStorage.removeItem('aiDataConsentDate');
    setHasConsent(null);
  };

  return {
    hasConsent,
    showConsentDialog,
    setShowConsentDialog,
    requestConsent,
    acceptConsent,
    declineConsent,
    resetConsent
  };
};

export default AIConsentDialog;
